import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrlWithPrefix } from '../config/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // Will be populated by fetchUser
  const [loading, setLoading] = useState(true);
  // Track consecutive 401 errors to avoid logging out on temporary issues
  const consecutive401ErrorsRef = React.useRef(0);
  const MAX_401_ERRORS = 3; // Allow up to 3 consecutive 401s before logging out

  const fetchUser = React.useCallback(async (forceRefresh = false, retryCount = 0) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const url = createApiUrlWithPrefix('auth/me');
      const urlWithCacheBuster = forceRefresh
        ? `${url}?t=${Date.now()}`
        : url;

      const response = await axios.get(urlWithCacheBuster, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      // Reset error counter on successful request
      consecutive401ErrorsRef.current = 0;

      const userData = response.data;
      console.log('🔄 AuthContext - Fetched user data:', userData);
      console.log('🖼️ AuthContext - User image paths:', {
        photoPath: userData.photoPath || userData.PhotoPath,
        imagePath: userData.imagePath || userData.ImagePath,
        updatedAt: userData.updatedAt || userData.UpdatedAt,
        rawData: {
          photoPath: userData.photoPath,
          PhotoPath: userData.PhotoPath,
          imagePath: userData.imagePath,
          ImagePath: userData.ImagePath
        }
      });
      console.log('AuthContext - User permissions:', userData.permissions);

      // Force state update to trigger re-renders
      setUser({ ...userData });
      // Store userId in localStorage
      localStorage.setItem('userId', response.data.id.toString());
    } catch (error) {
      const status = error.response?.status;

      // Handle 401 (Unauthorized) - but be more resilient
      if (status === 401) {
        consecutive401ErrorsRef.current++;
        console.warn(`⚠️ Authentication failed (401) - attempt ${consecutive401ErrorsRef.current}/${MAX_401_ERRORS}:`, {
          message: error.message,
          url: error.config?.url,
          retryCount
        });

        // Only log out after multiple consecutive 401 errors
        if (consecutive401ErrorsRef.current >= MAX_401_ERRORS) {
          console.error('❌ Multiple consecutive 401 errors - token appears invalid. Logging out...');
          consecutive401ErrorsRef.current = 0; // Reset counter
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        } else {
          // Retry after a short delay for temporary issues
          if (retryCount < 2) {
            console.log(`🔄 Retrying fetchUser after 401 error (attempt ${retryCount + 1})...`);
            setTimeout(() => {
              fetchUser(forceRefresh, retryCount + 1);
            }, 1000); // Wait 1 second before retry
          }
        }
      } else {
        // Reset 401 error counter on non-401 errors
        consecutive401ErrorsRef.current = 0;

        // For other errors (network, 500, etc.), just log but don't log out
        console.warn('⚠️ Failed to fetch user (non-auth error):', {
          status: status,
          message: error.message,
          url: error.config?.url
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial Auth Check
    const token = localStorage.getItem('token');

    if (token) {
      // 1. Restore auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // 2. Optimistically try to get user data
      fetchUser().catch(() => {
        // If fetch fails badly (not just 401), allow retry logic inside fetchUser to handle
        // or just accept we are in "loading" state until it resolves
      });
    } else {
      // No token found, we are definitely not logged in
      setLoading(false);
    }
  }, [fetchUser]);

  // Refresh user data when window regains focus (e.g., after uploading image from mobile)
  useEffect(() => {
    // Use a ref to throttle focus/visibility refreshes (max once every 30 seconds)
    const lastRefreshRef = { current: 0 };
    const THROTTLE_TIME = 30000;

    const handleFocus = () => {
      const now = Date.now();
      if (now - lastRefreshRef.current < THROTTLE_TIME) return;
      
      const token = localStorage.getItem('token');
      if (token) {
        console.log('🔄 Window focused - refreshing user data...');
        fetchUser(true);
        lastRefreshRef.current = now;
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now();
        if (now - lastRefreshRef.current < THROTTLE_TIME) return;

        const token = localStorage.getItem('token');
        if (token) {
          console.log('🔄 Tab became visible - refreshing user data...');
          fetchUser(true);
          lastRefreshRef.current = now;
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic refresh every 60 seconds (down from 5s)
    const refreshInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        const now = Date.now();
        if (now - lastRefreshRef.current < 45000) return; // Don't refresh if we just did it via focus
        
        console.log('🔄 Periodic refresh - updating user data...');
        fetchUser(true);
        lastRefreshRef.current = now;
      }
    }, 60000); 

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      console.log('🌐 Web Login - Starting login process...');
      console.log('🌐 Web Login - Email:', email);
      console.log('🌐 Web Login - Password length:', password.length);
      console.log('🌐 Web Login - API URL:', createApiUrlWithPrefix('auth/login'));
      console.log('🌐 Web Login - Full URL:', `${createApiUrlWithPrefix('auth/login')}`);

      const response = await axios.post(createApiUrlWithPrefix('auth/login'), { email, password });
      const { token, user: userData } = response.data;

      console.log('🌐 Web Login - Response received:', response.status);
      console.log('🌐 Web Login - Response data:', response.data);

      console.log('AuthContext - Login response:', response.data);
      console.log('AuthContext - User data from login:', userData);
      console.log('AuthContext - User role from login:', userData?.role, typeof userData?.role);
      console.log('AuthContext - User permissions from login:', userData?.permissions);

      localStorage.setItem('token', token);
      localStorage.setItem('userId', userData.id.toString());
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch full user data with permissions after login
      try {
        const meResponse = await axios.get(createApiUrlWithPrefix('auth/me'));
        console.log('AuthContext - Me response after login:', meResponse.data);
        console.log('AuthContext - User permissions from /me:', meResponse.data.permissions);
        setUser(meResponse.data);
        // Update userId in localStorage with the full user data
        localStorage.setItem('userId', meResponse.data.id.toString());
      } catch (meError) {
        console.error('AuthContext - Error fetching user data after login:', meError);
        // Fallback to basic user data if /me fails
        setUser(userData);
      }

      return { success: true };
    } catch (error) {
      console.error('🌐 Web Login - Error occurred:', error);
      console.error('🌐 Web Login - Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        timeout: error.config?.timeout
      });

      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('🔄 Manual refresh requested - fetching fresh user data...');
      await fetchUser(true); // Force refresh with cache-busting
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}





